// <head> карты /tutors: мета на языке страницы, canonical, hreflang, стили.
// Стили формы партнёров (tutor-join.css) нужны и здесь: поля и чипы общие.
import tmCss from "./tutors-map.css?url";
import tjCss from "./tutor-join.css?url";
import { MAP_COPY, TUTORS_PATH } from "./map-copy";
import { jsonLdText, partnerJsonLd } from "./partner-jsonld";
import { partnerMeta } from "./partner-meta";
import type { SiteLang } from "@/components/site/SiteChrome";
import { SITE_CHROME_LINKS } from "@/components/site/site-chrome-head";
import type { PublicPartner } from "@/lib/map-public.functions";
import { OG_LOCALE, socialMeta } from "@/lib/page-meta";
import { itemListJsonLd, profileBreadcrumbJsonLd, profileCrumbSubject, subjectBreadcrumbJsonLd, subjectPageText, subjectPath, type ListedPartner } from "@/lib/tutor-subject-pages";
import type { SubjectLoad } from "./TutorSubjectPage";

const SITE = "https://yolutap.com";

/** В ItemList хаба — не больше стольких карточек. */
const ITEM_LIST_MAX = 100;

/**
 * partner — анкета из loader профиля: title и description с её именем, JSON-LD.
 * Нет — общие тексты карты. list — карточки хаба из loader: JSON-LD ItemList.
 */
export function tutorsMapHead(lang: SiteLang, slug?: string, partner?: PublicPartner | null, list?: ListedPartner[] | null, pageCodes?: string[]) {
  const path = (l: SiteLang) => TUTORS_PATH[l] + (slug ? `/${slug}` : "");
  // Анкета (27.10.2026, Search Console: «страница является копией»): текст анкеты
  // один, три языковые версии почти одинаковы. Canonical всех трёх — версия на
  // языке, на котором анкета написана (map_partners.lang), без hreflang.
  const own = partner ? (partner.lang ?? lang) : null;
  const canonical = SITE + path(own ?? lang);
  return {
    meta: [...socialMeta(partner ? partnerMeta(partner, lang) : MAP_COPY[lang].meta), { property: "og:type", content: "website" }, { property: "og:url", content: canonical }, { property: "og:locale", content: OG_LOCALE[lang] }],
    links: [
      { rel: "canonical", href: canonical },
      // Карта и анкета без найденной строки — три языка, x-default — азербайджанская (27.10.2026).
      ...(partner
        ? []
        : [
            { rel: "alternate", hrefLang: "ru", href: SITE + path("ru") },
            { rel: "alternate", hrefLang: "az", href: SITE + path("az") },
            { rel: "alternate", hrefLang: "en", href: SITE + path("en") },
            { rel: "alternate", hrefLang: "x-default", href: SITE + path("az") },
          ]),
      ...SITE_CHROME_LINKS,
      { rel: "stylesheet", href: tjCss },
      { rel: "stylesheet", href: tmCss },
    ],
    // JSON-LD анкеты (Person / EducationalOrganization) — только на профиле с найденной анкетой.
    scripts: partner
      ? [
          { type: "application/ld+json", children: jsonLdText(partnerJsonLd(partner, lang)) },
          // Крошки профиля (29.10.2026): Yolutap → репетиторы → предмет → имя.
          { type: "application/ld+json", children: jsonLdText(profileBreadcrumbJsonLd(partner, lang, profileCrumbSubject(partner, pageCodes ?? []))) },
        ]
      : list && list.length
        ? [{ type: "application/ld+json", children: jsonLdText(itemListJsonLd(list.slice(0, ITEM_LIST_MAX), lang, MAP_COPY[lang].h1)) }]
        : [],
  };
}

/** <head> страницы предмета: свой title/description, canonical, hreflang, ItemList и BreadcrumbList. */
export function tutorSubjectHead(lang: SiteLang, subject: SubjectLoad) {
  const t = subjectPageText(subject.code, lang, subject.rows.length);
  const url = (l: SiteLang) => SITE + subjectPath(subject.code, l);
  return {
    meta: [...socialMeta({ title: t.title, description: t.description }), { property: "og:type", content: "website" }, { property: "og:url", content: url(lang) }, { property: "og:locale", content: OG_LOCALE[lang] }],
    links: [
      { rel: "canonical", href: url(lang) },
      { rel: "alternate", hrefLang: "ru", href: url("ru") },
      { rel: "alternate", hrefLang: "az", href: url("az") },
      { rel: "alternate", hrefLang: "en", href: url("en") },
      { rel: "alternate", hrefLang: "x-default", href: url("az") },
      ...SITE_CHROME_LINKS,
      { rel: "stylesheet", href: tjCss },
      { rel: "stylesheet", href: tmCss },
    ],
    scripts: [
      { type: "application/ld+json", children: jsonLdText(itemListJsonLd(subject.rows.slice(0, ITEM_LIST_MAX), lang, t.h1)) },
      { type: "application/ld+json", children: jsonLdText(subjectBreadcrumbJsonLd(subject.code, lang)) },
    ],
  };
}

/** <head> ответа 404 по /tutors/<slug>: «не найдена» на языке страницы, noindex, стили профиля. */
export function partnerNotFoundHead(lang: SiteLang) {
  return {
    meta: [{ title: `${MAP_COPY[lang].notFound.replace(/\.$/, "")} — Yolutap` }, { name: "robots", content: "noindex" }],
    links: [...SITE_CHROME_LINKS, { rel: "stylesheet", href: tjCss }, { rel: "stylesheet", href: tmCss }],
  };
}
